import { GridContainer, GridItem, GridRow } from "@tvg/fuse-design/responsive-grid";
import { SpacingWrapper } from "@tvg/fuse-design/spacing-wrapper";
import { VideoPlayer as VideoPlayerFuse } from "@tvg/fuse-design/video-player";
import useGlobalConfig from "client/context/GlobalConfig";
import buildVideoFrame from "client/utils/buildVideoFrame";
import buildVideoSources from "client/utils/buildVideoSources";
import getImageSources from "client/utils/getImageSources";
import React, { useMemo } from "react";
import { AmpVideoPlayer } from "types/amplience";

export interface VideoPlayerProps {
  config: AmpVideoPlayer;
  hideHeadlines?: boolean;
  ratio?: string;
  gridWrapper?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  config: { title, description, videoPlayer, posterOptions, captions },
  hideHeadlines = false,
  ratio = "16:9",
  gridWrapper = true,
  ...props
}) => {
  const { brand } = useGlobalConfig();

  const configProps = useMemo(() => {
    const populateCaptions =
      captions &&
      captions.map(({ url, label }) => ({
        src: url,
        label,
        type: "captions",
      }));

    const posterFrame = posterOptions?.posterFrame ? posterOptions.posterFrame : 1;
    const poster = posterOptions?.posterImage
      ? getImageSources(posterOptions.posterImage, {
          brand,
          aspect: "16:9",
          w: 740,
          qlt: "default",
        }).fallbackSource
      : buildVideoFrame(videoPlayer, posterFrame, brand);

    return {
      titleTag: "h2",
      title: hideHeadlines ? "" : title,
      description: hideHeadlines ? "" : description,
      poster,
      sources: buildVideoSources(videoPlayer, brand),
      tracks: populateCaptions,
      ratio,
    };
  }, [captions, posterOptions, videoPlayer, brand, title, description, hideHeadlines, ratio]);

  return gridWrapper ? (
    <SpacingWrapper marginBottom="l">
      <GridContainer>
        <GridRow>
          <GridItem xxs="12">
            <VideoPlayerFuse {...configProps} {...props} dataTestId="content-video-player" />
          </GridItem>
        </GridRow>
      </GridContainer>
    </SpacingWrapper>
  ) : (
    <VideoPlayerFuse {...configProps} {...props} dataTestId="content-video-player" />
  );
};

export default React.memo(VideoPlayer);
